import { Banner } from "bizpandit-ui";
import { Link } from "react-router-dom";
import { ROUTES } from "@/app/constants";
import { useJobHealth, problemJobs } from "../hooks/useJobHealth";

/**
 * A Banner when a background job is failing or has stopped running.
 *
 * WHY A BANNER AND NOT A TOAST. The guide's rule: a Toast is the outcome of
 * what the user just did; a Banner is a condition that is true now and stays
 * true until something changes it. A nightly job that has been failing for a
 * week is the second kind, and it is nobody's "just did".
 *
 * THIS IS NOT A PUSH NOTIFICATION AND SHOULD NOT BE MISTAKEN FOR ONE. Nothing
 * reaches anyone who does not open the app — MailService is a stub with no
 * transport installed, and a WhatsApp alert needs a Meta-approved template that
 * does not exist. This closes the gap between "recorded" and "seen", not the
 * one between "seen" and "told".
 *
 * Renders nothing while loading, and nothing on error. A viewer without
 * `audit:read` gets a 403, and a broken banner about broken jobs helps nobody.
 */
export function JobHealthBanner() {
  const { data, isError } = useJobHealth();
  if (isError) return null;

  const problems = problemJobs(data);
  if (problems.length === 0) return null;

  const worst = problems[0];
  const label: Record<string, string> = {
    FAILED: "failed on its last run",
    NEVER_RUN: "has never run",
    STALE: "has stopped running",
  };

  return (
    <Banner tone="danger" title="A background job needs attention" className="mb-3">
      <span>
        <strong>{worst.jobName}</strong> {label[worst.status] ?? worst.status}
        {worst.status === "STALE" && worst.hoursSinceSuccess !== null
          ? ` — last succeeded ${Math.round(worst.hoursSinceSuccess)} hours ago, and it should run every ${worst.expectedIntervalHours}`
          : ""}
        {worst.detail ? `. ${worst.detail}` : "."}
        {problems.length > 1 ? ` (${problems.length - 1} other job(s) also affected.)` : ""}
      </span>{" "}
      <Link to={ROUTES.jobHealth} className="underline font-medium">
        See all jobs
      </Link>
    </Banner>
  );
}
